"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { BoardCard, Subtask } from "@/lib/data";
import EditCardModal from "./EditCardModal";

const subtaskStatusConfig: Record<string, { label: string; color: string; next: string }> = {
  backlog: { label: "Backlog",     color: "text-[#888880] bg-[#f0f0f0]",           next: "active" },
  active:  { label: "In Progress", color: "text-[#b5622a] bg-[#fdf7f0]",           next: "waiting" },
  waiting: { label: "Waiting",     color: "text-[#6d559c] bg-[#f5f2fb]",           next: "done" },
  blocked: { label: "Blocked",     color: "text-[#8a3a3a] bg-[#fbf0f0]",           next: "active" },
  done:    { label: "Done",        color: "text-[#3a8a5a] bg-[#f0faf4]",           next: "backlog" },
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function BoardCardComponent({ card }: { card: BoardCard }) {
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [newSubtaskName, setNewSubtaskName] = useState("");
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState<"robby" | "weston" | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingAssignee, setEditingAssignee] = useState<string | null>(null);

  const subtasks = card.subtasks ?? [];
  const doneCount = subtasks.filter((s) => s.status === "done").length;
  const hasCompactMeta = Boolean(card.assignedTo || card.started || card.type === "project");

  async function patch(payload: object) {
    setSaving(true);
    await fetch("/api/projects", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: card.id, ...payload }),
    });
    router.refresh();
    setSaving(false);
  }

  async function cycleSubtaskStatus(subtask: Subtask) {
    const next = subtaskStatusConfig[subtask.status].next;
    await patch({ action: "update-subtask", subtaskId: subtask.id, status: next });
  }

  async function addSubtask() {
    if (!newSubtaskName.trim()) return;
    await patch({ action: "add-subtask", name: newSubtaskName, assignedTo: newSubtaskAssignee });
    setNewSubtaskName("");
    setNewSubtaskAssignee(null);
    setAddingSubtask(false);
  }

  async function updateSubtaskAssignee(subtaskId: string, assignedTo: "robby" | "weston" | null) {
    await patch({ action: "update-subtask", subtaskId, assignedTo });
    setEditingAssignee(null);
  }

  async function deleteSubtask(subtaskId: string) {
    await patch({ action: "delete-subtask", subtaskId });
  }

  return (
    <>
    <div className={`bg-white border rounded-lg transition-all ${saving ? "opacity-60" : "hover:shadow-sm hover:border-[#d4cdc3]"} border-[#e8e2d8]`}>
      {/* Card header */}
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {/* Type icon */}
            <span className="text-[13px] mt-0.5 flex-shrink-0" title={card.type === "project" ? "Project" : "Task"}>
              {card.type === "project" ? "📁" : "✅"}
            </span>
            <div className="min-w-0">
              <div className="font-mono text-[10px] font-semibold text-[#a0906e] leading-none mb-1">
                {card.ticketNumber}
              </div>
              <h2
                className="font-serif text-[15px] text-[#2c2416] leading-snug hover:text-[#b5622a] cursor-pointer transition-colors"
                onClick={() => setEditing(true)}
                title="Click to edit"
              >
                {card.name}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Type badge */}
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
              card.type === "project"
                ? "bg-[#f5f0fa] text-[#7a52a0] border-[#d4b8e8]"
                : "bg-[#f0f7fa] text-[#3a7a96] border-[#b0d4e4]"
            }`}>
              {card.type === "project" ? "Project" : "Task"}
            </span>
            <button
              onClick={() => setBodyExpanded(!bodyExpanded)}
              className="w-6 h-6 grid place-items-center rounded text-[#a0906e] hover:text-[#b5622a] hover:bg-[#fdf7f0] transition-colors"
              title={bodyExpanded ? "Collapse card details" : "Expand card details"}
              aria-label={bodyExpanded ? "Collapse card details" : "Expand card details"}
              aria-expanded={bodyExpanded}
            >
              <span className="text-[12px] leading-none">{bodyExpanded ? "▴" : "▾"}</span>
            </button>
          </div>
        </div>

        {hasCompactMeta && (
          <div className="mt-1.5 flex items-center gap-2 pl-[22px] text-[11px] text-[#b0a090]">
            {card.assignedTo && (
              <span>{card.assignedTo === "robby" ? "Robby" : "Weston"}</span>
            )}
            {card.assignedTo && (card.started || card.type === "project") && <span>·</span>}
            {card.started && <span>{formatDate(card.started)}</span>}
            {card.started && card.type === "project" && <span>·</span>}
            {card.type === "project" && (
              <span>{subtasks.length === 0 ? "No subtasks" : `${doneCount}/${subtasks.length} subtasks`}</span>
            )}
          </div>
        )}

        {bodyExpanded && (
          <div className="mt-3">
            {card.description && (
              <p className="text-[13px] text-[#6b5c48] leading-relaxed mb-3 pl-[22px]">
                {card.description}
              </p>
            )}

            {card.notes && (
              <p className="card-note-text text-[11.5px] text-[#a0906e] italic leading-relaxed mb-3 pl-[22px] border-l-2 border-[#e8c4a0] ml-[22px]">
                {card.notes}
              </p>
            )}

            {/* Assignee badge */}
            {card.assignedTo && (
              <div className="mt-2 pl-[22px]">
                <span className="text-[11px] px-2 py-1 rounded-full font-medium border" style={{
                  backgroundColor: card.assignedTo === "robby" ? "#f0f0f0" : "#f0f7fa",
                  color: card.assignedTo === "robby" ? "#666" : "#3a7a96",
                  borderColor: card.assignedTo === "robby" ? "#e0e0e0" : "#b0d4e4"
                }}>
                  👤 {card.assignedTo === "robby" ? "Robby" : "Weston"}
                </span>
              </div>
            )}

            {/* Subtask summary + toggle (projects only) */}
            {card.type === "project" && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-3 flex items-center gap-2 text-[12px] text-[#8a7a68] hover:text-[#b5622a] transition-colors pl-[22px]"
              >
                <span className="text-[10px]">{expanded ? "▾" : "▸"}</span>
                {subtasks.length === 0
                  ? "No subtasks"
                  : `${doneCount}/${subtasks.length} subtasks done`}
                {subtasks.length > 0 && (
                  <span className="ml-1 flex gap-0.5">
                    {subtasks.map((s) => (
                      <span
                        key={s.id}
                        className={`w-2 h-2 rounded-full ${
                          s.status === "done" ? "bg-[#3a8a5a]" :
                          s.status === "active" ? "bg-[#b5622a]" :
                          s.status === "waiting" || s.status === "blocked" ? "bg-[#8a6fbd]" : "bg-[#d8d8d0]"
                        }`}
                      />
                    ))}
                  </span>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Subtasks panel */}
      {card.type === "project" && bodyExpanded && expanded && (
        <div className="border-t border-[#f0ede8] px-5 pb-4 pt-3">
          <div className="flex flex-col gap-2 mb-3">
            {subtasks.map((subtask) => {
              const sc = subtaskStatusConfig[subtask.status] || subtaskStatusConfig.backlog;
              return (
                <div key={subtask.id} className="flex items-center gap-2 group">
                  {/* Status toggle */}
                  <button
                    onClick={() => cycleSubtaskStatus(subtask)}
                    className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sc.color} hover:opacity-80 transition-opacity`}
                    title="Click to cycle status"
                  >
                    {sc.label}
                  </button>
                  <span className={`text-[13px] flex-1 ${subtask.status === "done" ? "line-through text-[#b0a090]" : "text-[#2c2416]"}`}>
                    {subtask.name}
                  </span>
                  
                  {/* Assignee for subtask */}
                  {editingAssignee === subtask.id ? (
                    <select
                      autoFocus
                      value={subtask.assignedTo ?? ""}
                      onChange={(e) => updateSubtaskAssignee(subtask.id, e.target.value === "" ? null : (e.target.value as "robby" | "weston"))}
                      onBlur={() => setEditingAssignee(null)}
                      className="text-[10px] px-2 py-0.5 rounded border border-[#b5622a] focus:outline-none bg-white"
                    >
                      <option value="">Unassigned</option>
                      <option value="robby">Robby</option>
                      <option value="weston">Weston</option>
                    </select>
                  ) : (
                    <button
                      onClick={() => setEditingAssignee(subtask.id)}
                      className={`text-[10px] px-2 py-0.5 rounded font-medium opacity-0 group-hover:opacity-100 transition-all ${
                        subtask.assignedTo === "robby" ? "bg-[#f0f0f0] text-[#666]" :
                        subtask.assignedTo === "weston" ? "bg-[#f0f7fa] text-[#3a7a96]" :
                        "bg-[#f0ede8] text-[#8a7a68]"
                      }`}
                      title="Click to assign"
                    >
                      {subtask.assignedTo ? (subtask.assignedTo === "robby" ? "R" : "W") : "—"}
                    </button>
                  )}
                  
                  <button
                    onClick={() => deleteSubtask(subtask.id)}
                    className="opacity-0 group-hover:opacity-100 text-[#c0b8b0] hover:text-red-400 text-[12px] transition-all"
                    title="Remove subtask"
                  >
                    ×
                  </button>
                </div>
              );
            })}

            {subtasks.length === 0 && !addingSubtask && (
              <p className="text-[12px] text-[#c0b8b0] italic">No subtasks yet.</p>
            )}
          </div>

          {/* Add subtask inline */}
          {addingSubtask ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  type="text"
                  value={newSubtaskName}
                  onChange={(e) => setNewSubtaskName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addSubtask();
                    if (e.key === "Escape") { setAddingSubtask(false); setNewSubtaskName(""); setNewSubtaskAssignee(null); }
                  }}
                  placeholder="Subtask name…"
                  className="flex-1 text-[13px] border border-[#e4ddd0] rounded px-3 py-1.5 focus:outline-none focus:border-[#b5622a] bg-[#faf8f4]"
                />
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={newSubtaskAssignee ?? ""}
                  onChange={(e) => setNewSubtaskAssignee(e.target.value === "" ? null : (e.target.value as "robby" | "weston"))}
                  className="flex-1 text-[12px] border border-[#e4ddd0] rounded px-3 py-1.5 focus:outline-none focus:border-[#b5622a] bg-white"
                >
                  <option value="">Unassigned</option>
                  <option value="robby">Robby</option>
                  <option value="weston">Weston</option>
                </select>
                <button
                  onClick={addSubtask}
                  className="text-[12px] px-3 py-1.5 bg-[#b5622a] text-white rounded hover:bg-[#9e5424] transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={() => { setAddingSubtask(false); setNewSubtaskName(""); setNewSubtaskAssignee(null); }}
                  className="text-[12px] text-[#b0a090] hover:text-[#6b5c48] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddingSubtask(true)}
              className="text-[12px] text-[#b0a090] hover:text-[#b5622a] transition-colors flex items-center gap-1"
            >
              <span>+</span> Add subtask
            </button>
          )}
        </div>
      )}
    </div>

    {editing && <EditCardModal card={card} onClose={() => setEditing(false)} />}
    </>
  );
}
